import type MappableSemaphore from '../../async/mappableSemaphore.js';
import type ProgressBar from '../../console/progressBar.js';
import { ProgressBarSymbol } from '../../console/progressBar.js';
import FileFactory from '../../factories/fileFactory.js';
import ArchiveEntry from '../../models/files/archives/archiveEntry.js';
import type File from '../../models/files/file.js';
import type Options from '../../models/options.js';
import { TrimScanFiles } from '../../models/options.js';
import FsUtil from '../../utils/fsUtil.js';
import IntlUtil from '../../utils/intlUtil.js';
import Module from '../module.js';

/**
 * For every input {@link File} found, attempt to find any {@link ROMPadding} present and resolve
 * its padded checksums.
 */
export default class ROMTrimProcessor extends Module {
  private readonly options: Options;
  private readonly fileFactory: FileFactory;
  private readonly mappableSemaphore: MappableSemaphore;

  constructor(
    options: Options,
    progressBar: ProgressBar,
    fileFactory: FileFactory,
    mappableSemaphore: MappableSemaphore,
  ) {
    super(progressBar, ROMTrimProcessor.name);
    this.options = options;
    this.fileFactory = fileFactory;
    this.mappableSemaphore = mappableSemaphore;
  }

  /**
   * Process each {@link File}, finding any {@link ROMPadding} present.
   */
  async process(inputRomFiles: File[]): Promise<File[]> {
    if (inputRomFiles.length === 0) {
      return inputRomFiles;
    }

    if (!this.options.usingDats()) {
      // We don't care about detecting trimming if we're not matching to a DAT
      return inputRomFiles;
    }

    const filesThatNeedProcessing = inputRomFiles.filter((inputFile) =>
      this.fileNeedsProcessing(inputFile),
    ).length;
    if (filesThatNeedProcessing === 0) {
      this.prefixedLogger.trace('no ROMs can be trimmed');
      return inputRomFiles;
    }

    this.prefixedLogger.trace(
      `processing trimming in ${IntlUtil.toLocaleString(inputRomFiles.length)} ROM${inputRomFiles.length === 1 ? '' : 's'}`,
    );
    this.progressBar.setSymbol(ProgressBarSymbol.ROM_TRIMMING_DETECTION);
    this.progressBar.resetProgress(filesThatNeedProcessing);

    const parsedFiles = await this.mappableSemaphore.map(inputRomFiles, async (inputFile: File) => {
      if (!this.fileNeedsProcessing(inputFile)) {
        return inputFile;
      }

      this.progressBar.incrementInProgress();
      const childBar = this.progressBar.addChildBar({
        name: inputFile.toString(),
        total: inputFile.getSize(),
        progressFormatter: FsUtil.sizeReadable.bind(FsUtil),
      });

      let fileWithTrimming: File;
      try {
        fileWithTrimming = await this.getFile(inputFile, childBar);
      } catch (error) {
        this.prefixedLogger.error(
          `${inputFile.toString()}: failed to process ROM trimming: ${error}`,
        );
        fileWithTrimming = inputFile;
      } finally {
        childBar.delete();
      }
      this.progressBar.incrementCompleted();

      return fileWithTrimming;
    });

    const trimmedRomsCount = parsedFiles.filter(
      (romFile) => romFile.getPaddings().length > 0,
    ).length;
    this.prefixedLogger.trace(
      `found ${IntlUtil.toLocaleString(trimmedRomsCount)} trimmed ROM${trimmedRomsCount === 1 ? '' : 's'}`,
    );

    this.prefixedLogger.trace('done processing file trimming');
    return parsedFiles;
  }

  private fileNeedsProcessing(inputFile: File): boolean {
    if (this.options.shouldReadFileForTrimming(inputFile.getFilePath())) {
      return true;
    }

    if (this.options.getTrimScanFiles() === TrimScanFiles.NEVER) {
      return false;
    }

    if (
      !(inputFile instanceof ArchiveEntry) &&
      this.options.getTrimScanFiles() === TrimScanFiles.ALWAYS
    ) {
      return true;
    }

    if (
      (inputFile instanceof ArchiveEntry ||
        FileFactory.isExtensionArchive(inputFile.getFilePath())) &&
      !this.options.getTrimScanArchives()
    ) {
      return false;
    }

    // Is a power of two, so it isn't trimmed
    return !(inputFile.getSize() === 0 || (inputFile.getSize() & (inputFile.getSize() - 1)) === 0);
  }

  private async getFile(inputFile: File, progressBar: ProgressBar): Promise<File> {
    if (
      this.options.getTrimScanFiles() === TrimScanFiles.AUTO &&
      !this.options.shouldReadFileForTrimming(inputFile.getFilePath())
    ) {
      const fileSignature = await this.fileFactory.signatureFrom(inputFile, (progress, total) => {
        progressBar.setCompleted(progress);
        if (total !== undefined) {
          progressBar.setTotal(total);
        }
      });
      if (!fileSignature?.canBeTrimmed()) {
        // This file isn't known to be trimmable
        return inputFile;
      }
    }

    const paddings = await this.fileFactory.paddingsFrom(inputFile, (progress) => {
      progressBar.setCompleted(progress);
    });
    if (paddings.length === 0) {
      // This file isn't trimmed
      return inputFile;
    }

    return inputFile.withPaddings(paddings);
  }
}
